/* eslint-disable sonarjs/no-duplicate-string */
import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { MbsSize } from 'mbs-ui-kit';
import { ModalService, ModalSettings } from 'mbs-ui-kit/modal/modal.service';
import {
  GetPurchasePayload,
  GetTotalPayload,
  LicensesPricesData,
  LicenseTypePrice,
  LicenseTypesPricesMap
} from 'mbs-ui-shared@components/buy-license-modal/models/calculator';
import { Balance, MBSLicenseKind, PrePaymentParams } from 'mbs-ui-shared@components/components/models/license.model';
import { AddServiceLicenseComponent } from 'mbs-ui-shared@components/licenses/components/add-service-license/add-service-license.component';
import { LicenseRMMCEComponent } from 'mbs-ui-shared@components/licenses/components/rmm-ce-limit/license-rmm-ce.component';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AdministratorsFacade } from 'mbs-ui-shared@facades/administrators.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { AdministratorInCamelCase, LicenseType, PermissionsEnum } from 'mbs-ui-shared@models';
import {
  AdministratorLicense,
  ComputerGrantLicenseResultDTO,
  FilterLicenseAndAdminId,
  LicenseAssignToAdmin,
  LicenseTypeSummary
} from 'mbs-ui-shared@models/License';
import { PermissionFlags } from 'mbs-ui-shared@models/PermissionFlags';
import { AdministratorLicenseAvailabilityInfo } from 'mbs-ui-shared@models/administrators/administrator-license-availability-info';
import { AdministratorLicenseSummaryInfo } from 'mbs-ui-shared@models/licenses/administrator-license-summary-info';
import { LicenseSummary } from 'mbs-ui-shared@models/licenses/license-summary';
import { AssignLicenseDialogueData } from 'mbs-ui-shared@modules/rmm-licenses/components/assign-license-dialogue/assign-license-dialogue-data';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { LicensesStoreActions, LicensesStoreSelectors } from 'mbs-ui-shared@store/licenses';
import { CALCULATOR_COMPONENT_TOKEN } from 'mbs-ui-shared@tokens/calculator.token';
import { LicensesHelper } from 'mbs-ui-shared@utils/helpers/licenses-helper';
import moment from 'moment';
import { catchError, filter, finalize, from, map, noop, Observable, of, switchMap, take } from 'rxjs';
import { LicensesSignalFacade } from './licenses-signal.facade';

@Injectable({
  providedIn: 'root'
})
export class LicensesFacade {
  protected licensesService = inject(LicensesService);
  protected licensesSignalFacade = inject(LicensesSignalFacade);
  protected modal = inject(ModalService);
  protected i18nPipe = inject(I18NextPipe);
  protected auth = inject(AuthFacade);
  protected adminFacade = inject(AdministratorsFacade);
  protected router = inject(Router);
  protected ability = inject(AbilityService);
  protected store = inject(Store);

  public calculatorComponent = inject(CALCULATOR_COMPONENT_TOKEN);

  public balance = this.store.select(LicensesStoreSelectors.selectBalance);
  public loadingBalance = this.store.select(LicensesStoreSelectors.selectLoadingBalance);

  public permissionFlags = this.store.select(LicensesStoreSelectors.selectPermissionFlags);
  public permissionFlagsLoaded = this.store.select(LicensesStoreSelectors.selectPermissionFlagsLoaded);

  public assignedLicenseByAdmin = (adminId: string, forceUpdate = false) =>
    (forceUpdate ? this.adminFacade.directActions.getEntityById(adminId) : of(this.adminFacade.entityById(adminId)())).pipe(
      map((admin) => ({ adminId: admin?.id, license: admin?.license }) as LicenseAssignToAdmin)
    );
  public loading = this.store.select(LicensesStoreSelectors.selectLoading);

  public isInvalidLicenses = this.store.select(LicensesStoreSelectors.isInvalidLicenses);

  public hasCloudRestoreLicense$ = toObservable(this.licensesSignalFacade.hasRestoreToCloudLicense);

  public subscriptionEditLink = this.store.select(LicensesStoreSelectors.selectSubscriptionEditLink);
  public loadingSubscriptionEditLink = this.store.select(LicensesStoreSelectors.selectLoadingSubscriptionEditLink);

  public loadTypes(loadPrividerTypesAlso = false) {
    this.licensesSignalFacade.loadAllLicenseTypes();
    loadPrividerTypesAlso && this.licensesSignalFacade.loadPrePostLicenseTypesByOwner();
  }

  public loadBalance() {
    this.store.dispatch(LicensesStoreActions.loadBalance());
  }

  public loadPermissionFlags(force = false) {
    this.store.dispatch(LicensesStoreActions.loadPermissionFlags({ force }));
  }

  public loadSubscriptionEditLink() {
    this.store.dispatch(LicensesStoreActions.loadSubscriptionEditLink());
  }

  public grantLicenseByType(hid: string, type: string): Observable<unknown> {
    return this.licensesService.grantLicenseByType(hid, type);
  }

  public assignLicenseToAdmin(administratorId: string, licenseType: LicenseType, isTrial: boolean): Observable<AdministratorLicense> {
    return this.handleLoading(
      this.licensesService
        .assignLicense(administratorId, licenseType, isTrial)
        .pipe(switchMap(() => this.updateAdministratorLicense(administratorId)))
    );
  }

  public getPrePaymentLicenses(params?: PrePaymentParams) {
    return this.licensesService.getPrePaymentLicenses(params);
  }

  public assignLicenseToAdmins(params: {
    licenseType: LicenseType;
    isTrial: boolean;
    administratorIds?: string[];
    search?: string;
  }): Observable<AdministratorLicense[]> {
    return this.handleLoading(this.licensesService.assignBulkLicenses(params).pipe(switchMap(() => this.updateAdministratorsLicenses())));
  }

  public getAvailableLicenseSummaries(): Observable<AdministratorLicenseSummaryInfo[]> {
    return this.handleLoading(this.licensesService.getAvailableLicenseSummaries());
  }

  protected getFilteredLicenseItems(filter: LicenseType[]): Observable<Array<AdministratorLicenseSummaryInfo>> {
    return this.getAvailableLicenseSummaries().pipe(
      switchMap((licenses) => of(filter?.length ? licenses.filter((x) => filter.includes(x.licenseType)) : licenses))
    );
  }

  protected assignLicenseOrOpenDialog$(data: FilterLicenseAndAdminId, isRmm: boolean): Observable<AdministratorLicense | boolean> {
    return this.hasAvailableLicense(isRmm, data).pipe(
      switchMap((hasAvailableLicense) => {
        return hasAvailableLicense && isRmm
          ? this.assignLicenseToAdmin(data.adminID, isRmm ? LicenseType.RMM : LicenseType.RemoteDesktop, false)
          : this.openAdminLicenseModal(data);
      })
    );
  }

  protected hasAvailableLicense(isRmm: boolean, data: FilterLicenseAndAdminId) {
    return this.getFilteredLicenseItems(data.filter).pipe(
      switchMap((licenses) => {
        const rmmLicense = licenses?.find((l) => l.licenseType === LicenseType.RMM);
        const rdLicense = !isRmm ? licenses?.find((l) => l.licenseType === LicenseType.RemoteDesktop) : null;
        return of(isRmm ? rmmLicense?.count : (!rdLicense && rmmLicense?.count) || (!rmmLicense && rdLicense?.count));
      })
    );
  }

  protected assignLicense(data: FilterLicenseAndAdminId, isRmm: boolean): Observable<null> {
    const trialRmm = this.isTrialLicenseAvailable(LicenseType.RMM);
    const trialRD = this.isTrialLicenseAvailable(LicenseType.RemoteDesktop);
    const licenseType = isRmm ? LicenseType.RMM : trialRD ? LicenseType.RemoteDesktop : LicenseType.RMM;

    return (isRmm ? trialRmm : trialRmm || trialRD)
      ? this.assignLicenseToAdmin(data.adminID, licenseType, true).pipe(map(() => null))
      : this.assignLicenseOrOpenDialog$(data, isRmm).pipe(map(() => null));
  }

  public grantLicense(licenseType: LicenseType, entityId: string, trial: boolean): Observable<AdministratorLicense> {
    return this.handleLoading(
      this.assignLicenseToAdmin(entityId, licenseType, trial).pipe(
        switchMap((license) => this.auth.fetchCurrentUser$.pipe(map(() => license)))
      )
    );
  }

  public removeLicense(administratorId: string): Observable<AdministratorLicense> {
    return this.handleLoading(
      this.licensesService.removeLicense(administratorId).pipe(switchMap(() => this.updateAdministratorLicense(administratorId)))
    );
  }

  public updateAdministratorLicense(administratorId: string): Observable<AdministratorLicense> {
    return this.adminFacade.directActions.getEntityById(administratorId).pipe(
      map((res) => res.license),
      switchMap((license) => this.auth.fetchCurrentUser$.pipe(map(() => license)))
    );
  }

  public updateAdministratorsLicenses(): Observable<null> {
    this.adminFacade.loadData(true, false);

    return this.auth.fetchCurrentUser$.pipe(map(() => null));
  }

  public isTrialLicenseAvailable(licenseType: LicenseType = null): boolean {
    const trialLicenses = this.auth?.currentUserValue?.AdminTrialLicences;

    if (!trialLicenses) {
      return false;
    }

    const isValidLicense = (license: AdministratorLicenseAvailabilityInfo) => {
      return license && license.Allowed && (!license.ExpiredDate || moment(license.ExpiredDate).isAfter(new Date()));
    };

    if (!isNil(licenseType)) {
      return isValidLicense(trialLicenses[licenseType]);
    } else {
      return Object.values(trialLicenses).some((license) => isValidLicense(license));
    }
  }

  public isLicenseExpired(license: AdministratorLicense): boolean {
    return LicensesHelper.IsLicenseExpired(license);
  }

  public buyLicenseByType(mode: 'rmm' | 'connect') {
    const rules = {
      ['rmm']: this.ability.can('read', PermissionsEnum.RmmPerEndpoint) ? [LicenseType.RmmConnectPE] : [LicenseType.RMM],
      ['connect']: this.ability.can('read', PermissionsEnum.RmmPerEndpoint)
        ? [LicenseType.RmmConnectPE, LicenseType.ConnectPE]
        : [LicenseType.RMM, LicenseType.RemoteDesktop]
    };

    return this.proceed(rules[mode]);
  }

  public buyLicense(licenses: string | string[] = 'rd,rmm'): void {
    const param = Array.isArray(licenses) ? licenses.join(',') : licenses;

    location.href = `${RoutingPath.ApCalc}?buy=true&licenses=${param}`;
  }

  public refreshAuth(): void {
    this.auth.fetchCurrentUser$.pipe(take(1)).subscribe();
  }

  protected prepareOpenGrantLicenseDialogueModalSettings(
    adminId: string,
    filter: LicenseType[] = null,
    withTrials = false,
    defaultFilter = true,
    email = ''
  ): ModalSettings {
    const settings = new ModalSettings();
    settings.responsive = true;
    settings.data = {
      title: this.i18nPipe.transform('shared:licenses.manageModal:assignTitle', {
        format: 'title'
      }),
      entityId: adminId,
      adminEmail: email,
      startTrial: false,
      showTrials: withTrials,
      filterLicensesFunc$: defaultFilter ? this.filterLicensesFunc.bind(this) : this.filterLicensesFuncForRM.bind(this),
      showBuyBtn: this.ability.can('read', PermissionsEnum.LicenseBuy)
    } as AssignLicenseDialogueData;
    if (filter && filter.length) settings.data.licenseTypesFilter = filter;

    return settings;
  }

  public assignLicenseDialogueModal(data: FilterLicenseAndAdminId): Observable<LicenseAssignToAdmin> {
    const isRmm = data.filter && data.filter.length === 1 && data.filter[0] === LicenseType.RMM;

    return this.getFilteredLicenseItems(data.filter).pipe(
      take(1),
      switchMap(() => this.handleAssignLicense(isRmm, data)),
      switchMap(() => this.assignedLicenseByAdmin(data.adminID))
    );
  }

  public tryGrantLicense(hid: string): Observable<ComputerGrantLicenseResultDTO> {
    return this.licensesService.tryGrantLicense(hid);
  }

  protected handleAssignLicense(isRmm, data: FilterLicenseAndAdminId) {
    return data.expiredCurrent ? this.assignLicenseOrOpenDialog$(data, isRmm) : this.assignLicense(data, isRmm);
  }

  protected prepareAssignLicenseModalText(isRmm: boolean, action: string): string {
    const product = this.i18nPipe.transform(isRmm ? 'shared:products.rmm' : 'shared:products.remote_desktop');

    const text = this.ability.can('read', PermissionsEnum.Licenses)
      ? this.i18nPipe.transform('shared:licenses.assignLicenseNow', { action })
      : '<br>Please contact your service provider.';

    return `A license is required to use <b>${product}</b>. ${text}`;
  }

  protected filterLicensesFuncForRM(adminId: string, licenses: LicenseSummary[]): Observable<LicenseSummary[]> {
    return of(true).pipe(
      switchMap(() => {
        licenses.forEach((license) => (license.canTrial = this.isTrialLicenseAvailable(license.licenseType)));

        return of(licenses);
      })
    );
  }

  protected filterLicensesFunc(adminId: string, licenses: LicenseSummary[]): Observable<LicenseSummary[]> {
    this.adminFacade.loadById({ id: adminId });
    return of(this.adminFacade.entityById(adminId)()).pipe(
      filter(Boolean),
      switchMap((admin: AdministratorInCamelCase) => {
        const isRMM = admin?.license?.licenseType === LicenseType.RMM;

        licenses.forEach((license) => {
          license.canTrial = this.isTrialLicenseAvailable(license.licenseType);
          license.show = isRMM && license.licenseType === LicenseType.RMM;
        });

        return of(licenses);
      })
    );
  }

  public openGrantLicenseDialogueWithInfoModal(data: FilterLicenseAndAdminId, action?: string): Observable<LicenseAssignToAdmin> {
    return this.openAssignLicenseModal(data, action).pipe(
      switchMap(({ result, isRmm }) => (result ? this.handleAssignLicense(isRmm, data) : of(null))),
      catchError(() => {
        window?.sessionStorage?.removeItem('btnClientId');
        return of(null);
      }),
      switchMap(() => this.assignedLicenseByAdmin(data.adminID))
    );
  }

  public openGrantLicenseDialogue(
    adminId: string,
    filter: LicenseType[] = null,
    withTrials = false,
    defaultFilter = true,
    email = '',
    modal?: any
  ): Observable<boolean> {
    const settings = this.prepareOpenGrantLicenseDialogueModalSettings(adminId, filter, withTrials, defaultFilter, email);

    return from(this.modal.openCustom(modal, settings)).pipe(
      map(() => true),
      catchError(() => of(null))
    );
  }

  public openAdminLicenseModal(data: FilterLicenseAndAdminId): Observable<boolean> {
    console.log('Method not implemented');
    throw new Error('Method not implemented');
  }

  public prepareAssignLicenseModalSettings(action: string): ModalSettings {
    const modalSettings: ModalSettings = {
      header: { title: this.i18nPipe.transform('shared:licenses.common.licenseRequired') },
      footer: {
        okButton: { show: false },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.cancel'), type: 'secondary' }
      }
    };

    if (this.ability.can('read', PermissionsEnum.Licenses)) {
      modalSettings.footer.okButton = {
        text: this.i18nPipe.transform(action),
        type: 'success'
      };
    }

    return modalSettings;
  }

  public openAssignLicenseModal(data: FilterLicenseAndAdminId, action: string) {
    const isRmm = data.filter && data.filter.length === 1 && data.filter[0] === LicenseType.RMM;

    const modalSettings = this.prepareAssignLicenseModalSettings(action);
    const modalText = this.prepareAssignLicenseModalText(isRmm, action);

    return from(this.modal.open(modalSettings, modalText)).pipe(map((result) => ({ result, isRmm })));
  }

  private handleLoading<T>(observable: Observable<T>): Observable<T> {
    this.store.dispatch(LicensesStoreActions.setLoading({ loading: true }));
    return observable.pipe(finalize(() => this.store.dispatch(LicensesStoreActions.setLoading({ loading: false }))));
  }

  public openRMMCELimitModal(): Observable<boolean> {
    return from(this.modal.openCustom(LicenseRMMCEComponent)).pipe(
      take(1),
      map(() => true),
      catchError(() => of(false))
    );
  }

  public getTypes(): Observable<LicenseTypeSummary[]> {
    return this.licensesService.getTypes();
  }

  public getPermissionFlags(): Observable<PermissionFlags> {
    return this.licensesService.getPermissionFlags();
  }

  public getBalance(): Observable<Balance> {
    return this.licensesService.getBalance();
  }

  public getPrices(allowedLicenses?: Array<LicenseType>): Observable<LicensesPricesData> {
    return this.licensesService.getPrices().pipe(
      map((data) => this.getPricesForAllowedLicenses(data, allowedLicenses)),
      map((data) => {
        return {
          ...data,
          licenseTypesPricesMap: this.getPricesMap(data.licenseTypesPrices)
        };
      })
    );
  }

  private getPricesForAllowedLicenses(data: Omit<LicensesPricesData, 'licenseTypesPricesMap'>, allowedLicenses?: Array<LicenseType>) {
    if (allowedLicenses) {
      data.licenseTypesPrices = data.licenseTypesPrices.filter((license) => allowedLicenses.includes(license.licenseType));
    }

    return data;
  }

  public getPricesMap(data: Array<LicenseTypePrice>) {
    return Object.fromEntries(data.map((item) => [item.licenseType, item])) as LicenseTypesPricesMap;
  }

  public getTotals(payload: GetTotalPayload) {
    return this.licensesService.getTotals(payload);
  }

  public getPurchase(payload: GetPurchasePayload) {
    return this.licensesService.getPurchase(payload);
  }

  public requestToSwitchToAnotherMode(toAnnual: boolean) {
    return this.licensesService.requestToSwitchToAnotherMode(toAnnual);
  }

  public invalidateLicenses() {
    this.store.dispatch(LicensesStoreActions.invalidateLicenses());
  }

  public proceed(allowedLicenses?: Array<LicenseType>) {
    return this.openBuyLicenseModal(allowedLicenses, null, null, false);
  }

  public proceedWithAutoAssign(licenseType?: LicenseType, hid?: string) {
    return this.openBuyLicenseModal([licenseType], licenseType, hid, true);
  }

  private openBuyLicenseModal(allowedLicenses?: Array<LicenseType>, licenseType?: LicenseType, hid?: string, preselected = false) {
    return this.modal
      .openCustom(this.calculatorComponent, {
        size: MbsSize.xl,
        data: {
          licenseType,
          hid,
          allowedLicenses,
          preselected
        }
      })
      .then(() => {
        this.invalidateLicenses();
        this.invalidateLicenseTypes();
        this.auth.fetchCurrentUser$.pipe(take(1)).subscribe();
        return true;
      })
      .catch(noop);
  }

  public invalidateLicenseTypes(): void {
    this.licensesSignalFacade.invalidateLicenseTypes();
  }

  public addServiceLicenseModal(serviceKind: MBSLicenseKind, preselect?: LicenseType, needReload?: boolean): void {
    const params: ModalSettings = {
      data: { title: this.i18nPipe.transform('shared:licenses.licenseModal.title'), kind: serviceKind, preselect },
      size: MbsSize.sm
    };

    this.modal
      .openCustom(AddServiceLicenseComponent, params)
      .then((result: unknown) => {
        if (!result) return;

        this.invalidateLicenseTypes();
        this.auth.fetchCurrentUser$.pipe(take(1)).subscribe();

        if (needReload) window?.location?.reload();
      })
      .catch(noop);
  }
}
